import React from "react";
import { useSelector } from "react-redux";
import {
    translateTransaction,
    AnonymityCallout,
    PrivacyOptionsCallout,
    getAnonymousCallout,
    getPrivacyCalloutMessage,
} from "@qgiv/core-donor";
import { constants } from "@qgiv/core-js";
import { selectAllFormSettings } from "../../../redux/slices/formSettingsSlice";
import { selectAllP2pSettings } from "../../../redux/slices/p2pSettingsSlice";
import { selectReceipt } from "../../../redux/slices/receiptSlice";
import { selectCmsControlByIdByPage } from "../../../redux/slices/cmsSettingsSlice";

/**
 * @param {object} props
 * @param {number} props.controlId
 * @returns {React.ReactElement} The rendered ConnectedAnonymityCallout component.
 */
const ConnectedAnonymityCallout = ({ controlId }) => {
    const {
        ENUMS: {
            EntityType,
            FieldType,
            ProductType,
            DefaultView,
            ActivityRole,
        },
    } = constants;
    const formSettings = useSelector((state) => selectAllFormSettings(state));
    const receipt = useSelector(selectReceipt);

    const { transaction = {} } = receipt;
    const { registrations = [] } = transaction;
    const { displayAnonymousDonorInformation } =
        useSelector(selectAllP2pSettings);
    const { type = ProductType.HOBNOB, isStandardDonationForm = 0 } =
        formSettings;
    const control = useSelector((state) =>
        selectCmsControlByIdByPage(
            controlId,
            DefaultView.STANDARD_REGISTRATION_RECEIPT,
            state,
        ),
    );

    const { options = {} } = control;

    // Convert any of the data that is on the transaction object that is a
    // number to a string before it is displayed on the receipt page
    const transactionWithDataAsStrings = translateTransaction(transaction);
    const { donations } = transactionWithDataAsStrings;

    // -------------------------------------------------------------------------
    // NOTE: Extra Privacy callouts should only be shown on donations made as a
    // NFP with either team or classification.
    // -------------------------------------------------------------------------
    const isNfp = registrations[0].activityRole === ActivityRole.NONFUNDRAISING;

    const entityType =
        donations[0]?.classification &&
        Number(donations[0]?.entityType) !== EntityType.TEAM
            ? EntityType.CLASSIFICATION
            : Number(donations[0]?.entityType);
    const selectedRecipient =
        isNfp && donations[0]?.entityName && entityType
            ? {
                  title: donations[0].entityName,
                  entityType,
                  captains: [],
              }
            : {};

    let shouldShowAmount = false;
    let shouldShowName = false;
    if (donations[0]) {
        shouldShowName = donations[0]?.showName;
        shouldShowAmount = donations[0]?.showAmount;
    }

    const anonymousCallOut = getAnonymousCallout({
        options,
        shouldShowAmount,
        shouldShowName,
        type,
    });
    const displayAnonymityCallout = !shouldShowName || !shouldShowAmount;

    // Consolidate props into object
    const anonymityCalloutProps = {
        anonymousCallOut,
        displayAnonymityCallout,
    };

    const privacyOptionsAmountMessage = getPrivacyCalloutMessage({
        fieldType: FieldType.SHOW_AMOUNT,
        selectedRecipient,
        displayAnonymousDonorInformation,
    });
    const privacyOptionsNameMessage = getPrivacyCalloutMessage({
        fieldType: FieldType.SHOW_NAME,
        selectedRecipient,
        displayAnonymousDonorInformation,
    });

    return (
        <div className="grid">
            <AnonymityCallout {...anonymityCalloutProps} />
            {displayAnonymousDonorInformation && isStandardDonationForm ? (
                <>
                    {!shouldShowName ? (
                        <PrivacyOptionsCallout
                            message={privacyOptionsNameMessage}
                        />
                    ) : null}
                    {!shouldShowAmount ? (
                        <PrivacyOptionsCallout
                            message={privacyOptionsAmountMessage}
                        />
                    ) : null}
                </>
            ) : null}
        </div>
    );
};

export default ConnectedAnonymityCallout;
